import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { TrackingApi } from 'api/TrackingApi';
import { getScreenOrientationWithSizes } from 'helpers/device';
import { useAppRouteEvents } from 'hooks/useAppRouteEvents';
import { useLocationChange } from 'hooks/useLocationChange';
import {
  QueryKeys,
  useSystemSearchQueries,
} from 'hooks/useSystemSearchQueries';
import { getIsCommonStateLoading } from 'store/common/selectors';
import { getUpgradePackageCampaignId } from 'store/payment/selectors';

const DYNAMIC_PAYMENT_PATH = /^\/payment\/[^/]+$/;

export const usePageView = (isAuthorized: boolean) => {
  const history = useHistory();
  const { pathname, search } = useLocation();

  const campaignId = useSelector(getUpgradePackageCampaignId);
  const isCommonLoading = useSelector(getIsCommonStateLoading);

  const { getBillingTokenQuery, getUpgradeOfferStatusQuery } =
    useSystemSearchQueries();
  const { value: billingQueryValue } = getBillingTokenQuery();
  const { value: upgradeStatusQueryValue } = getUpgradeOfferStatusQuery();

  const getCampaignId = useCallback(() => {
    if (
      ['/upgrade', '/upgrade_trial', '/payment', '/payment-photo'].includes(
        pathname
      ) ||
      DYNAMIC_PAYMENT_PATH.test(pathname)
    ) {
      return campaignId;
    }

    return undefined;
  }, [campaignId, pathname]);

  const handleTrackPageView = useCallback(
    ({ currentUrl }) => {
      if (isAuthorized && !isCommonLoading) {
        TrackingApi.trackPageView({
          url: currentUrl,
          info: upgradeStatusQueryValue || undefined,
          billingToken: billingQueryValue || undefined,
          campaign_id: getCampaignId(),
          screen: getScreenOrientationWithSizes(),
        }).catch((err) => err);
      }
    },
    [
      billingQueryValue,
      getCampaignId,
      isAuthorized,
      isCommonLoading,
      upgradeStatusQueryValue,
    ]
  );

  const handleTrackPageLoad = useCallback(
    ({ duration }: { duration: number }) => {
      TrackingApi.trackPageLoad({
        url: window.location.href,
        info: upgradeStatusQueryValue || undefined,
        duration: Math.round(duration),
        screen: getScreenOrientationWithSizes(),
      })
        .catch((err) => err)
        .finally(() => {
          if (search.includes(QueryKeys.UpgradeOfferStatus)) {
            const newSearch = queryString.exclude(search, [
              QueryKeys.UpgradeOfferStatus,
            ]);
            history.replace({ search: newSearch });
          }
        });
    },
    [history, search, upgradeStatusQueryValue]
  );

  useAppRouteEvents({
    onLoadEnd: handleTrackPageLoad,
  });

  useLocationChange(handleTrackPageView);
};
